import { Portal, Select } from '@mantine/core';
import classnames from 'classnames';
import { useMemo, useState } from 'react';
import { useLocalStorage } from 'react-use';
import { FixedSizeGrid as Grid } from 'react-window';
import { invokeEvent } from 'shared-base';
import { sendAnalytics } from '../../globals';
import { useBoundingBox } from '../../hooks/useBox';
import { useCustomEvent } from '../../hooks/useCustomEvent';
import { useFuzzySearchQ } from '../../hooks/useFuzzySearch';
import IconButton from '../IconButton/IconButton';
import IconCategories from '../IconCategories/IconCategories';
import MaterialCategoryPicker from '../MaterialCategoryPicker/MaterialCategoryPicker';
import MaterialPreview from '../MaterialPreview/MaterialPreview';
import Search from '../Search/Search';
import { MaterialSelectorCell } from './MaterialSelector.components';
import { styles } from './MaterialSelector.data';
import { Categories, Category, Content, Filter, Row, Top, Wrapper } from './MaterialSelector.style';

export type MaterialSelectorProps = {
  value: string;
  materials: IMaterial[];
  onSelect: (material: any) => void;
};

export function MaterialSelector(props: MaterialSelectorProps) {
  const { value, materials } = props;
  const [q, setQ] = useState('');
  const [ref, pos] = useBoundingBox();
  const { width, height } = pos;
  const [category, setCategory] = useLocalStorage('MATERIAL_CATEGORY', 'all');
  const [style, setStyle] = useLocalStorage('MATERIAL_STYLE', 'all');
  const [showGrid, setShowGrid] = useState(true);

  function onChangeStyle(value: string) {
    setStyle(value);
    sendAnalytics('Material Style Change', { value });
  }

  const filtered = useMemo(() => {
    return materials.filter((t: IMaterial) => {
      const okCategory = category === 'all' || t.category === category;
      const okStyle = style === 'all' || t.style === style;

      return okCategory && okStyle;
    });
  }, [materials, category, style]);

  const data = useFuzzySearchQ(filtered, ['name', 'shortDescription', 'category'], q);

  const columnCount = Math.floor(width / 58);

  const Cell = (itemProps: any) => {
    const itemIndex = itemProps.rowIndex * columnCount + itemProps.columnIndex;
    const item = data[itemIndex] as Json;

    if (!item) return null;

    const { index } = item;

    return (
      <MaterialSelectorCell
        key={index}
        item={item}
        itemProps={itemProps}
        value={value}
        onSelect={props.onSelect}
      />
    );
  };

  const className = classnames('MaterialSelector-wrapper', {});

  useCustomEvent('MaterialSelector/resize', () => {
    setShowGrid(false);
    setTimeout(() => setShowGrid(true), 100);
  });

  function renderGrid() {
    if (!width) return null;
    if (!showGrid) return;
    return (
      <Grid
        columnCount={columnCount}
        rowCount={Math.ceil(data.length / columnCount)}
        columnWidth={58}
        rowHeight={64}
        width={width}
        height={height}
        itemData={data}
        overscanColumnCount={20} // Preload 2 extra columns on each side
        overscanRowCount={20} // Preload 2 extra rows on each side
      >
        {Cell}
      </Grid>
    );
  }

  function showPreview() {
    invokeEvent('material/preview', { open: true, item: materials[0] });
  }

  function renderCategoryPicker() {
    const left = pos.left - 170 + 'px';
    const top = 0 + 'px';

    if (!pos.isReady) return null;

    return (
      <Portal>
        <Categories style={style}>
          <MaterialCategoryPicker value={category} onChange={setCategory} />
        </Categories>
      </Portal>
    );
  }

  return (
    <Wrapper className={className} data-testid='MaterialSelector-wrapper'>
      <Top>
        <Row>
          <Select
            placeholder='Filter by style'
            data={styles}
            size='xs'
            value={style}
            onChange={onChangeStyle}
            style={{ flex: 1 }}
          />
          <IconButton color='#668' name='IconMicroscope' onClick={showPreview} />
        </Row>
        <Row>
          <Search
            q={q}
            onClear={() => setQ('')}
            onSearch={setQ}
            placeholder='Search texture...'
            flex
          />
          <Filter>
            <IconCategories value={category} onChange={setCategory} />
          </Filter>
        </Row>
      </Top>
      <Content className='materials-gallery-content' ref={ref}>
        {renderGrid()}
      </Content>
      <MaterialPreview />
      <Category category={category}>{category}</Category>
      {renderCategoryPicker()}
    </Wrapper>
  );
}

export default MaterialSelector;
